// /home/cno/dev/WS_MERCALINK_FRONT/front/src/app/sales/quotation/custom-FSE/fse-processor.service.ts
import { Injectable, inject } from '@angular/core';
import { AlertService } from '@app/shared/alert.service';
import { DocType } from '@app/shared/constants';
import { SalesOrderState } from '@app/shared/constants-server';
import { MatDialog } from '@angular/material/dialog';
import { BsDialogComponent, FactureFR, ProcessingResult } from 'mercalink-fselib';
import { environment } from '@app/../environments/environment';
import { LoginService } from '@app/common/login.service';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { RecipientsComponent, OutputDialogData } from '@app/fse/recipients/recipients.component';
import { showError } from '@app/shared/util';
import { Observable, of } from 'rxjs';
import { catchError, filter, switchMap, tap, map } from 'rxjs/operators';
import { Station } from '@app/shared/_models/common';
import Swal from 'sweetalert2';

/**
 * Defines the contract for actions that the QuotationComponent must provide
 * for the QuotationFseProcessorService to interact with it.
 */
export interface QuotationFseActions {
  patchFormGroupState: (state: SalesOrderState) => void;
  patchFormGroupCustomer: (customer: number) => void;
  patchFormGroupDocType?: (docType: DocType) => void;
  patchFormGroupFseId?: (fseId: number) => void;
  submitForm: (cb?: Function) => void;
  openPartnerDialog: (idpartner: Number) => void;
  triggerActionBarAction: (action: String) => void;
  /**
   * [release-fse-orthop] Callback invoked after securing a re-submitted FSE on a migrated order
   * (`state === MIGRATION`) to refresh the read-only order and its `fseOrders` list without mutating `sales_order`.
   */
  reloadAfterMigrationFse?: () => void;
}

// Interface for the result of BsDialogComponent.afterClosed()
interface BsDialogResult {
  station: Station;
  factureFR: FactureFR;
  processingResult?: ProcessingResult;
  fse_id?: number;
  etat_facture?: string;
}

export interface PatientCreationResponse {
  data: {
    partner_id: number;
    patient_id: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class QuotationFseProcessorService {
  private dialog: MatDialog = inject(MatDialog);
  private loginService: LoginService = inject(LoginService);
  private quotationService: QuotationService = inject(QuotationService);

  constructor(private alertService: AlertService) {}

  /**
   * Processes the successful creation of a partner after an FSE event.
   * This method encapsulates the logic for updating the order state,
   * showing success messages, and triggering further UI actions.
   *
   * @param response The response from the postHCCreatePartnerEvent, containing partner_id.
   * @param actions An object containing callback functions for component-specific actions.
   * @param fseId Optional FSE order ID to attach to the sales order.
   * @param isLocation Whether the current order is a LOCATION contract.
   * @param isMigration [release-fse-orthop] Whether the current order is a sealed Orthop migration order (`state === MIGRATION`).
   * @param isReissue Whether the current order is an already sealed order where an FSE is being reissued after rejection.
   */
  processSuccessfulFseCreation(
    response: PatientCreationResponse,
    actions: QuotationFseActions,
    fseId?: number,
    isLocation: boolean = false,
    isMigration: boolean = false,
    isReissue: boolean = false,
  ): void {
    // [release-fse-orthop] Isolated path for Orthop migrated invoices (`sales_order.state === 'MIGRATION'`)
    // or reissued FSE on sealed order:
    // Historical/sealed invoices are sealed in `safe.transaction`.
    // Never mutate `sales_order` (no state transition, no doc_type change, no customer patch, no delivery creation).
    // The FSE row (`fse_order`), `sales_order.data.fse_id`, and open AMO/AMC `sales_receivable` payers are already updated server-side
    // by `fse::migration_fse::rebind_migration_receivables_if_needed`.
    if (isMigration || isReissue) {
      this.alertService.success(isMigration ? 'FSE sécurisée et rattachée à la facture reprise.' : 'FSE sécurisée et rattachée à la commande.');
      actions.reloadAfterMigrationFse?.();
      return;
    }

    actions.patchFormGroupCustomer(response.data.partner_id);
    if (fseId && actions.patchFormGroupFseId) {
      actions.patchFormGroupFseId(fseId);
    }

    if (isLocation) {
      // In a LOCATION context, the order is a standing contract.
      // Transition to PREPARATION to lock the contract against accidental edits, but do NOT transition to PAID (which seals it).
      actions.patchFormGroupState(SalesOrderState.PREPARATION);
      actions.submitForm(() => {
        this.alertService.success('Patient et modèle FSE rattachés au contrat de location (contrat verrouillé en préparation).');
        actions.openPartnerDialog(response.data.partner_id);
      });
      return;
    }

    // Standard sale: align with location by transitioning doc_type to FACTURE_FSE
    if (actions.patchFormGroupDocType) {
      actions.patchFormGroupDocType(DocType.FACTURE_FSE);
    }

    // Choix du mode de délivrance pour la vente FSE :
    // Conformément aux règles BOFIP et au choix fonctionnel, toute commande FSE doit passer par une livraison effective.
    // Deux scénarios se présentent :
    // 1. Emport immédiat (comptoir) : Le patient repart directement avec le matériel.
    //    On soumet directement la commande à l'état INVOICED en un seul appel atomique.
    //    Le serveur crée en une seule transaction SQL : le BL comptoir livré, le scellement safe.transaction et les créances.
    // 2. Livraison à programmer : Le matériel doit être livré à domicile.
    //    On passe la commande en PREPARATION (générant les créances), puis on ouvre la fiche client pour que l'opérateur
    //    renseigne l'adresse de livraison du patient, ce qui débloquera la programmation du BL dans DeliveryComponent.
    Swal.fire({
      title: 'Délivrance de la FSE',
      text: 'Le matériel est-il emporté sur-le-champ (livraison comptoir) ou à programmer en livraison ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Emport immédiat (comptoir)',
      cancelButtonText: 'Programmer une livraison',
      confirmButtonColor: '#1976d2',
      cancelButtonColor: '#388e3c',
      allowOutsideClick: false,
    }).then(result => {
      if (result.isConfirmed) {
        // Scénario 1 : Emport immédiat -> Soumission unique atomique vers INVOICED
        // Côté backend, `handle_state_entry_effects` crée simultanément :
        // - Le BL comptoir (delivery) marqué livré (realdatdel = now()) et facturé (datinvoicing = now())
        // - La transaction fiscale scellée (safe.transaction)
        // - Les créances (sales_receivable AMO / AMC / Patient)
        actions.patchFormGroupState(SalesOrderState.INVOICED);
        actions.submitForm(() => {
          this.alertService.success('FSE sécurisée : BL comptoir livré, facture et créances validées.');
          actions.openPartnerDialog(response.data.partner_id);
        });
      } else {
        // Scénario 2 : Livraison à programmer -> Transition vers PREPARATION
        // Verrouille les lignes, crée les créances, et ouvre la fiche client pour renseigner l'adresse de livraison.
        // L'opérateur pourra ensuite cliquer sur « Planifier livraison » pour créer et programmer le BL.
        actions.patchFormGroupState(SalesOrderState.PREPARATION);
        actions.submitForm(() => {
          this.alertService.info('FSE sécurisée en préparation. Veuillez renseigner l’adresse du patient pour planifier la livraison.');
          actions.openPartnerDialog(response.data.partner_id);
        });
      }
    });
  }

  /**
   * Opens the FSE (Facturation Sécurisée Electronique) dialog.
   * This method encapsulates the logic for interacting with the FSE library's BsDialogComponent,
   * processing its results, and initiating partner creation if successful.
   *
   * @param orderId The ID of the current sales order.
   * @param areaBaseUrl The base URL for the FSE area server.
   * @param userId The ID of the logged-in user.
   * @param currentStation The current station configuration.
   * @param configArea The FSE configuration for the area.
   * @param outputDialogData Data from the recipient selection dialog.
   * @param numIdentFact The identification number for invoicing.
   * @param componentActions An object containing callback functions for component-specific actions.
   * @param isLocation Whether the order is a LOCATION contract.
   * @returns An Observable that emits the API response after successful partner creation, or null if an error occurs or dialog is cancelled.
   */
  openBsDialogAndProcessFSE(
    orderId: number,
    areaBaseUrl: string | undefined,
    userId: number | undefined,
    currentStation: Station | undefined,
    configArea: any, // Use a more specific type if available
    outputDialogData: OutputDialogData | number | undefined,
    numIdentFact: string | undefined,
    componentActions: QuotationFseActions,
    isLocation: boolean = false,
    isMigration: boolean = false,
    isReissue: boolean = false,
  ): Observable<any | null> {
    const dialogRef = this.dialog.open(BsDialogComponent, {
      disableClose: true,
      height: 'unset',
      maxWidth: 'unset',
      data: {
        server_url: environment.server_url,
        area_baseurl: areaBaseUrl,
        order_id: orderId,
        fse_id: undefined,
        userid: userId,
        outputDialogData: outputDialogData,
        station: currentStation,
        configarea: configArea,
        numIdentFact: numIdentFact,
        isLocation: isLocation,
      },
    });

    return dialogRef.afterClosed().pipe(
      filter((result): result is BsDialogResult => !!result),
      tap(bsDialogResult => {
        if (bsDialogResult.station) {
          this.loginService.currentstation = bsDialogResult.station;
        }
      }),
      filter(bsDialogResult => {
        if (isLocation) {
          const hasBeneficiaire = !!bsDialogResult.factureFR?.beneficiaire;
          const isTemplateValidated = bsDialogResult.etat_facture === 'MODELE_LOCATION';
          const isTarifiedOrSaved = bsDialogResult.processingResult?.areaResponse?.HTTPResponse?.codeRetour === '0' || !!bsDialogResult.fse_id;
          const isSuccess = hasBeneficiaire && (isTemplateValidated || isTarifiedOrSaved);
          if (!isSuccess) {
            showError('La saisie ou tarification du modèle FSE a été annulée');
          }
          return isSuccess;
        }
        const isSuccess = bsDialogResult.processingResult?.areaResponse?.HTTPResponse?.codeRetour === '0';
        if (!isSuccess) {
          showError('La facturation FSE a échoué ou a été annulée');
        }
        return isSuccess;
      }),
      switchMap(bsDialogResult => {
        console.log('BS dialog resulting processingResult: ' + JSON.stringify(bsDialogResult.processingResult, null, 2));
        const fseId = bsDialogResult.fse_id || (bsDialogResult.factureFR?.numFacture ? parseInt(bsDialogResult.factureFR.numFacture, 10) : undefined);
        return this.quotationService.postHCCreatePartnerEvent({ beneficiaire: bsDialogResult.factureFR.beneficiaire }).pipe(
          map(response => ({ response, fseId })),
          catchError(e => {
            showError("Le patient n'a pas pu être créé comme client");
            return of(null);
          }),
        );
      }),
      filter((item): item is { response: PatientCreationResponse; fseId: number | undefined } => {
        if (!item || !item.response) return false;
        const response = item.response;
        const isValid = typeof response === 'object' && 'data' in response && !!response.data && typeof response.data.partner_id === 'number' && typeof response.data.patient_id === 'string';
        if (!isValid) {
          showError('Format de réponse invalide lors de la création du client');
          return false;
        }
        return true;
      }),
      tap(({ response, fseId }) => {
        // Now call the existing service method to process the successful partner creation with fseId
        this.processSuccessfulFseCreation(response, componentActions, fseId, isLocation, isMigration, isReissue);
      }),
      map(({ response }) => response), // Return the response for the component to potentially use, or just complete
    );
  }

  /**
   * Initiates the FSE processing flow by first opening the recipient selection dialog (RecipientsComponent).
   * If a recipient is successfully chosen, it then proceeds to open the FSE dialog (BsDialogComponent)
   * and handles the subsequent partner creation and order state updates.
   *
   * @param orderId The ID of the current sales order.
   * @param areaBaseurl The base URL for the FSE area server.
   * @param userId The ID of the logged-in user.
   * @param currentStation The current station configuration.
   * @param configArea The FSE configuration for the area.
   * @param numIdentFact The identification number for invoicing.
   * @param componentActions An object containing callback functions for component-specific actions.
   * @param isLocation Whether the order is a LOCATION contract.
   * @param isMigration [release-fse-orthop] Whether the order is a sealed Orthop migration order (`state === MIGRATION`).
   * @param isReissue Whether the order is an already sealed order where an FSE is being reissued after rejection.
   * @returns An Observable that emits the API response after successful partner creation, or null if an error occurs or dialog is cancelled.
   */
  initiateFSEProcess(
    orderId: number,
    areaBaseurl: string | undefined,
    userId: number | undefined,
    currentStation: Station | undefined,
    configArea: any, // Use a more specific type if available
    numIdentFact: string | undefined,
    componentActions: QuotationFseActions,
    isLocation: boolean = false,
    isMigration: boolean = false,
    isReissue: boolean = false,
  ): Observable<any | null> {
    const dialogRef = this.dialog.open(RecipientsComponent, {
      width: '533px',
      height: '400px',
      maxWidth: 'unset',
      data: { area_baseurl: areaBaseurl, server_url: environment.server_url, userid: userId, numIdentFact: numIdentFact },
    });

    return dialogRef.afterClosed().pipe(
      filter((outputDialogData: OutputDialogData | undefined): outputDialogData is OutputDialogData => !!outputDialogData), // Filter out undefined/cancel results
      switchMap((outputDialogData: OutputDialogData) => {
        console.log('[initiateFSEProcess] Recipient chosen, opening BS dialog.');
        return this.openBsDialogAndProcessFSE(orderId, areaBaseurl, userId, currentStation, configArea, outputDialogData, numIdentFact, componentActions, isLocation, isMigration, isReissue);
      }),
    );
  }
}
